/*
 * external-checkout.js — Storefront handoff for the self-hosted Authorize.net checkout.
 *
 * WHAT IT DOES
 *   Intercepts every "checkout" intent on the Shopify storefront and, instead of
 *   letting the browser navigate to Shopify's native /checkout, it:
 *     1. reads the live cart via the Shopify AJAX API (GET /cart.js),
 *     2. POSTs the line items to the external checkout backend (POST {ORIGIN}/api/sessions),
 *     3. redirects the browser to the returned checkout_url.
 *
 *   Shopify's cart stays the single source of truth for line items + bundle
 *   properties (_bundle_id, _bundle_size, _bundle_tier, _bundle_discount); the
 *   backend re-prices everything server-side. Client amounts are never sent.
 *
 * CONFIG
 *   The backend origin is read (in order of precedence) from:
 *     1. window.EXTERNAL_CHECKOUT_ORIGIN  (set an inline <script> before this file), or
 *     2. the `data-origin` attribute on this script's own <script> tag.
 *   If neither is set, checkout is BLOCKED and a visible console error is logged
 *   (fail-safe: we never silently fall through to Shopify's /checkout).
 *
 * ROBUSTNESS
 *   All interception is done via a single delegated listener on `document` in the
 *   CAPTURE phase, so it also catches buttons/links injected later by the remote
 *   Kenso runtime (product grids, header cart, mini-cart) that would otherwise
 *   navigate to /checkout.
 *
 * PUBLIC API
 *   window.ExternalCheckout.redirect()  -> Promise<void>  (kick off checkout manually)
 *   window.ExternalCheckout.origin      -> string | null  (resolved backend origin)
 */
(function () {
  'use strict';

  var GLOBAL_KEY = 'ExternalCheckout';

  // ---------------------------------------------------------------------------
  // Resolve the backend origin.
  // ---------------------------------------------------------------------------
  function resolveOrigin() {
    // 1. Explicit global set by the theme.
    if (
      typeof window.EXTERNAL_CHECKOUT_ORIGIN === 'string' &&
      window.EXTERNAL_CHECKOUT_ORIGIN.trim() !== ''
    ) {
      return normalizeOrigin(window.EXTERNAL_CHECKOUT_ORIGIN.trim());
    }
    // 2. data-origin on our own <script> tag.
    var self =
      document.currentScript ||
      (function () {
        var scripts = document.getElementsByTagName('script');
        for (var i = scripts.length - 1; i >= 0; i--) {
          var src = scripts[i].getAttribute('src') || '';
          if (src.indexOf('external-checkout') !== -1) return scripts[i];
        }
        return null;
      })();
    if (self) {
      var attr = self.getAttribute('data-origin');
      if (attr && attr.trim() !== '') return normalizeOrigin(attr.trim());
    }
    return null;
  }

  function normalizeOrigin(value) {
    // Strip a trailing slash so we can safely concatenate paths.
    return value.replace(/\/+$/, '');
  }

  var ORIGIN = resolveOrigin();

  function configError() {
    // Visible, unmissable error — never fail silently into Shopify's checkout.
    console.error(
      '[external-checkout] BACKEND ORIGIN NOT CONFIGURED. ' +
        'Set window.EXTERNAL_CHECKOUT_ORIGIN or add data-origin="https://<checkout-domain>" ' +
        'to the <script src=".../external-checkout.js"> tag. Checkout is blocked until this is fixed.'
    );
  }

  // ---------------------------------------------------------------------------
  // Perceived-speed helpers: warm the backend connection ahead of time, and show
  // an instant full-screen loader the moment checkout is clicked so the ~0.5s of
  // session-creation never feels like a dead click.
  // ---------------------------------------------------------------------------
  function preconnect(url) {
    try {
      var pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = url;
      pc.crossOrigin = 'anonymous';
      (document.head || document.documentElement).appendChild(pc);
      var dns = document.createElement('link');
      dns.rel = 'dns-prefetch';
      dns.href = url;
      (document.head || document.documentElement).appendChild(dns);
    } catch (_e) {
      /* non-fatal */
    }
  }

  function showCheckoutOverlay() {
    if (document.getElementById('external-checkout-overlay')) return;
    if (!document.getElementById('external-checkout-overlay-style')) {
      var style = document.createElement('style');
      style.id = 'external-checkout-overlay-style';
      style.textContent =
        '#external-checkout-overlay{position:fixed;inset:0;z-index:2147483647;display:flex;' +
        'flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
        'background:rgba(0,0,0,0.6);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);' +
        'opacity:0;transition:opacity .15s ease}' +
        '#external-checkout-overlay.is-visible{opacity:1}' +
        '#external-checkout-overlay .ec-ovl__spinner{width:44px;height:44px;border-radius:50%;' +
        'border:3px solid rgba(255,255,255,.25);border-top-color:#fff;animation:ec-ovl-spin .7s linear infinite}' +
        '#external-checkout-overlay .ec-ovl__text{color:#fff;font-size:15px;font-weight:600;letter-spacing:.01em;' +
        'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif}' +
        '@keyframes ec-ovl-spin{to{transform:rotate(360deg)}}';
      (document.head || document.documentElement).appendChild(style);
    }
    var overlay = document.createElement('div');
    overlay.id = 'external-checkout-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML =
      '<div class="ec-ovl__spinner"></div><div class="ec-ovl__text">Taking you to secure checkout…</div>';
    document.body.appendChild(overlay);
    // next frame → fade in
    requestAnimationFrame(function () {
      overlay.classList.add('is-visible');
    });
  }

  function hideCheckoutOverlay() {
    var overlay = document.getElementById('external-checkout-overlay');
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  // ---------------------------------------------------------------------------
  // Meta pixel glue. The pixel itself is installed by the theme; here we (a)
  // fire InitiateCheckout at the moment of handoff and (b) forward the pixel's
  // first-party cookies (_fbp/_fbc) to the backend so the server-side CAPI
  // Purchase can be attributed to the ad click. All best-effort — tracking must
  // never block checkout.
  // ---------------------------------------------------------------------------
  function getCookie(name) {
    try {
      var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : undefined;
    } catch (_e) {
      return undefined;
    }
  }

  function trackInitiateCheckout(cart, items) {
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('track', 'InitiateCheckout', {
        content_type: 'product',
        content_ids: items.map(function (i) { return String(i.variant_id); }),
        value: (cart.total_price || 0) / 100,
        currency: cart.currency || 'USD',
        num_items: cart.item_count || items.length,
      });
    } catch (_e) {
      /* never block checkout on tracking */
    }
  }

  // ---------------------------------------------------------------------------
  // Core redirect flow.
  // ---------------------------------------------------------------------------
  var inFlight = false;

  function redirect() {
    if (!ORIGIN) {
      configError();
      return Promise.reject(new Error('external-checkout: backend origin not configured'));
    }
    if (inFlight) {
      // A checkout is already being created; ignore duplicate triggers.
      return Promise.resolve();
    }
    inFlight = true;
    showCheckoutOverlay();

    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (res) {
        if (!res.ok) {
          throw new Error('external-checkout: /cart.js responded ' + res.status);
        }
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && Array.isArray(cart.items) ? cart.items : []).map(function (i) {
          return {
            variant_id: i.variant_id,
            quantity: i.quantity,
            properties: i.properties || null,
          };
        });

        if (items.length === 0) {
          throw new Error('external-checkout: cart is empty');
        }

        trackInitiateCheckout(cart, items);

        return fetch(ORIGIN + '/api/sessions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          // Cross-origin call to the checkout backend (CORS-allowlisted there).
          mode: 'cors',
          // fbp/fbc: Meta pixel cookies for CAPI attribution (undefined keys
          // are dropped by JSON.stringify — nothing is sent when absent).
          body: JSON.stringify({ items: items, fbp: getCookie('_fbp'), fbc: getCookie('_fbc') }),
        });
      })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (body) {
            throw new Error(
              'external-checkout: POST /api/sessions responded ' + res.status + ' ' + body
            );
          });
        }
        return res.json();
      })
      .then(function (data) {
        if (!data || typeof data.checkout_url !== 'string' || data.checkout_url === '') {
          throw new Error('external-checkout: response missing checkout_url');
        }
        window.location.assign(data.checkout_url);
      })
      .catch(function (err) {
        inFlight = false;
        hideCheckoutOverlay();
        console.error('[external-checkout] checkout failed:', err);
        // Surface a minimal, dependency-free notice so the buyer isn't stuck silently.
        try {
          window.alert(
            'Sorry — we could not start checkout. Please try again in a moment.'
          );
        } catch (_e) {
          /* alert unavailable (e.g. sandboxed) — the console error above stands */
        }
        throw err;
      });
    // NB: on success we intentionally leave `inFlight = true`; the page is
    // navigating away, so we must not allow a second session to be created.
  }

  // ---------------------------------------------------------------------------
  // Interception. Single delegated listener, capture phase, on `document`.
  // ---------------------------------------------------------------------------

  // Does this element (or an ancestor within `root`) match a checkout trigger?
  function matchTrigger(target) {
    var el = target;
    while (el && el !== document && el.nodeType === 1) {
      // Cart-page checkout submit button.
      if (el.matches('button[name="checkout"], input[name="checkout"]')) return el;
      // Mini-cart checkout link + any anchor pointing at Shopify /checkout.
      if (el.matches('.mini-cart__checkout, a[href="/checkout"], a[href^="/checkout"]')) {
        return el;
      }
      // Absolute links to the store's own /checkout (Kenso may render these).
      if (el.tagName === 'A') {
        var href = el.getAttribute('href') || '';
        if (isCheckoutHref(href)) return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  function isCheckoutHref(href) {
    if (!href) return false;
    if (href.charAt(0) === '/') return href.indexOf('/checkout') === 0;
    // Absolute URL on the same host pointing at /checkout.
    try {
      var u = new URL(href, window.location.href);
      return (
        u.host === window.location.host && u.pathname.indexOf('/checkout') === 0
      );
    } catch (_e) {
      return false;
    }
  }

  function onClickCapture(event) {
    if (event.defaultPrevented) return;

    var trigger = matchTrigger(event.target);
    if (!trigger) return;

    // A checkout trigger was matched. Intercept it for ANY click — including
    // middle-click and cmd/ctrl/shift/alt-click — so a modified click on a
    // "/checkout" link can't open Shopify's native checkout in a new tab.
    event.preventDefault();
    event.stopPropagation();
    redirect().catch(function () {
      /* already logged + alerted in redirect() */
    });
  }

  function onSubmitCapture(event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!form || form.nodeType !== 1) return;
    // The cart page form posts to /cart with a name="checkout" submitter.
    var action = form.getAttribute('action') || '';
    var isCartForm = /\/cart\b/.test(action) || form.matches('form[action*="/cart"]');
    var hasCheckoutSubmit = !!form.querySelector('[name="checkout"]');
    var submitter = event.submitter || null;
    var submitterIsCheckout =
      submitter && submitter.matches && submitter.matches('[name="checkout"]');

    if ((isCartForm && hasCheckoutSubmit) || submitterIsCheckout) {
      event.preventDefault();
      event.stopPropagation();
      redirect().catch(function () {
        /* handled in redirect() */
      });
    }
  }

  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('submit', onSubmitCapture, true);

  // On success we leave `inFlight = true` because the page is navigating away.
  // But when the buyer clicks Back, the browser restores this page from the
  // bfcache with JS state intact — so `inFlight` would still be `true` and the
  // next checkout click would be silently ignored. `pageshow` fires on every
  // display, including bfcache restores; reset the guard so checkout works again.
  window.addEventListener('pageshow', function () {
    inFlight = false;
    hideCheckoutOverlay(); // clear a restored loader if the buyer navigated back
  });

  // ---------------------------------------------------------------------------
  // Defense-in-depth: hide Shopify's dynamic/express checkout buttons (Shop Pay,
  // Apple Pay, Google Pay, PayPal, "Buy it now"). Those launch Shopify's own
  // express checkout via an iframe/JS that our click/submit interception cannot
  // catch, so they'd bypass Authorize.net entirely. Removing `{{ form |
  // payment_button }}` in the theme is the primary fix (see README); this hides
  // any that slip through.
  // ---------------------------------------------------------------------------
  function hideExpressButtons() {
    if (document.getElementById('external-checkout-hide-express')) return;
    var style = document.createElement('style');
    style.id = 'external-checkout-hide-express';
    style.textContent =
      '.shopify-payment-button,.dynamic-checkout__content,' +
      '[data-shopify="dynamic-checkout-cart"],[data-shopify="payment-button"]' +
      '{display:none !important}';
    (document.head || document.documentElement).appendChild(style);
  }
  hideExpressButtons();

  // ---------------------------------------------------------------------------
  // Public API.
  // ---------------------------------------------------------------------------
  window[GLOBAL_KEY] = {
    redirect: redirect,
    origin: ORIGIN,
  };

  if (!ORIGIN) {
    // Log once at load so misconfiguration is obvious even before a click.
    configError();
  } else {
    // Warm the TLS connection to the checkout backend now, so the POST that
    // creates the session on click doesn't pay the handshake cost.
    preconnect(ORIGIN);
  }
})();
